import React from "react";
import Link from "next/link";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { SectionHeader } from "../components/section-header";
import { SectionLoader } from "../components/section-loader";
import { EmptyState } from "@/components/shared/empty-state";
import { Users, MessageSquare } from "lucide-react";

interface CommunityHighlightsSectionProps {
  items: any[];
  isLoading: boolean;
}

export function CommunityHighlightsSection({ items, isLoading }: CommunityHighlightsSectionProps) {
  const displayItems = items.length > 0 ? items.slice(0, 4) : [];

  if (!displayItems.length && !isLoading) return null;

  return (
    <section className="border-b border-border" aria-labelledby="community-heading">
      <div className="mx-auto max-w-7xl px-5 py-16 sm:py-20">
        <SectionHeader
          eyebrow="Community"
          icon={Users}
          title="Community Highlights"
          href="/community"
        />
        {isLoading ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-36 bg-neutral-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : displayItems.length > 0 ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {displayItems.map((item: any) => (
              <Link key={item.id} href={`/communities/${item.slug ?? item.id}`}>
                <Card className="group h-full hover:border-neutral-300 transition-all duration-150">
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-neutral-100 text-foreground">
                        <Users className="h-5 w-5" />
                      </div>
                      <div>
                        <CardTitle className="text-sm">{item.name}</CardTitle>
                        <CardDescription className="text-xs mt-0.5">
                          {item.memberCount ?? 0} members
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">{item.description}</p>
                    <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
                      <MessageSquare className="h-3 w-3" />
                      <span>Join the conversation</span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <EmptyState icon={Users} description="No communities yet." />
        )}
      </div>
    </section>
  );
}